// A fixed, nested lattice adds dots without moving the ones already on the nail.
// 100% matches the original 18 x 18 pattern exactly. Density only selects more
// cells from the same 36 x 36 tile; erased IDs survive later density changes.
export const DOT_DENSITY = Object.freeze({min:25,max:400,step:25,default:100});
export const DOT_SIZE = Object.freeze({min:.5,max:20,step:.5,default:5});
export const DOT_RADIUS = 2.5;
export const DOT_TILE_SIZE = 36;
export const DOT_PATTERN_BOUNDS = Object.freeze({x:-100,y:-150,width:300,height:450});
const SPACING=9;
const CELLS=Object.freeze([
  [1,1],[3,3],[3,1],[1,3],
  [0,0],[2,2],[0,2],[2,0],
  [0,1],[2,3],[0,3],[2,1],
  [1,0],[3,2],[3,0],[1,2],
]);
const finite=(value,fallback)=>typeof value==='number'&&Number.isFinite(value)?value:fallback;
const mod=(value,divisor)=>(value%divisor+divisor)%divisor;

export function normalizeDotDensity(value) {
  return Math.round(Math.max(DOT_DENSITY.min,Math.min(DOT_DENSITY.max,finite(value,DOT_DENSITY.default)))/DOT_DENSITY.step)*DOT_DENSITY.step;
}

// Repeating dots used a fixed radius before dotSize existed. Never infer their
// size from the old strokeWidth field, which previously had no visual effect.
export function normalizeDotSize(value) {
  return Math.max(DOT_SIZE.min,Math.min(DOT_SIZE.max,finite(value,DOT_SIZE.default)));
}

export function isHandpaintDot(layer) {
  const first=layer?.points?.[0];
  return Boolean(layer?.kind==='stroke'&&layer.brush!=='fill'&&first&&layer.points.every(point=>Math.hypot(point.x-first.x,point.y-first.y)<.05));
}

export function dotSizeForLayers(layers=[],fallback=DOT_SIZE.default) {
  const visible=layer=>layer.visible!==false&&layer.opacity!==0;
  const pattern=layers.findLast(layer=>visible(layer)&&layer.kind==='pattern'&&layer.type==='dots');
  if(pattern)return normalizeDotSize(pattern.dotSize);
  const dot=layers.findLast(layer=>visible(layer)&&isHandpaintDot(layer));
  return normalizeDotSize(dot?.strokeWidth??fallback);
}

export function resizeNailDots(nail,size) {
  const dotSize=normalizeDotSize(size);
  return {layers:nail.layers.map(layer=>layer.kind==='pattern'&&layer.type==='dots'?{...layer,dotSize}:isHandpaintDot(layer)?{...layer,strokeWidth:dotSize}:layer)};
}

export function dotFromId(id) {
  if(typeof id!=='string'||!/^[-]?\d{1,2}:[-]?\d{1,2}$/.test(id))return null;
  const [column,row]=id.split(':').map(Number),x=column*SPACING,y=row*SPACING,b=DOT_PATTERN_BOUNDS;
  const margin=DOT_SIZE.max/2;
  if(id!==`${column}:${row}`||x<b.x-margin||x>b.x+b.width+margin||y<b.y-margin||y>b.y+b.height+margin)return null;
  return {id,column,row,x,y,r:DOT_RADIUS};
}

export function normalizeDotPattern(value={}) {
  const erasedDots=Array.isArray(value.erasedDots)?[...new Set(value.erasedDots.slice(0,2048).filter(id=>dotFromId(id)))]:[];
  const dotSizes=value.dotSizes&&typeof value.dotSizes==='object'&&!Array.isArray(value.dotSizes)?Object.fromEntries(Object.entries(value.dotSizes).slice(0,2048).filter(([id,size])=>dotFromId(id)&&typeof size==='number'&&Number.isFinite(size)).map(([id,size])=>[id,normalizeDotSize(size)])):{};
  return {dotDensity:normalizeDotDensity(value.dotDensity),dotSize:normalizeDotSize(value.dotSize),erasedDots,dotFlipX:value.dotFlipX===true,...(Object.keys(dotSizes).length?{dotSizes}:{})};
}

export function dotPatternTile(density=DOT_DENSITY.default,size=DOT_SIZE.default) {
  return CELLS.slice(0,normalizeDotDensity(density)/DOT_DENSITY.step).map(([column,row])=>({x:column*SPACING,y:row*SPACING,r:normalizeDotSize(size)/2}));
}

export function patternDotPoints(layer,{includeErased=false}={}) {
  const {dotDensity,dotSize,erasedDots,dotSizes={}}=normalizeDotPattern(layer),b=DOT_PATTERN_BOUNDS,r=Math.max(dotSize,...Object.values(dotSizes))/2;
  const cells=new Set(CELLS.slice(0,dotDensity/DOT_DENSITY.step).map(([column,row])=>`${column}:${row}`));
  const erased=new Set(includeErased?[]:erasedDots),points=[];
  for(let row=Math.ceil((b.y-r)/SPACING);row<=Math.floor((b.y+b.height+r)/SPACING);row++){
    for(let column=Math.ceil((b.x-r)/SPACING);column<=Math.floor((b.x+b.width+r)/SPACING);column++){
      const id=`${column}:${row}`;
      const radius=(dotSizes[id]??dotSize)/2,x=column*SPACING,y=row*SPACING;
      if(cells.has(`${mod(column,4)}:${mod(row,4)}`)&&!erased.has(id)&&x+radius>=b.x&&x-radius<=b.x+b.width&&y+radius>=b.y&&y-radius<=b.y+b.height)points.push({id,column,row,x,y,r:radius});
    }
  }
  return points;
}

export function dotPatternMask(layer) {
  const {erasedDots}=normalizeDotPattern(layer);
  if(!erasedDots.length)return {removed:[],restored:[]};
  const points=patternDotPoints(layer,{includeErased:true}),byId=new Map(points.map(dot=>[dot.id,dot]));
  const erased=new Set(erasedDots),removed=erasedDots.map(id=>byId.get(id)).filter(Boolean),restored=new Map();
  // Removing one large dot must retain the overlapping parts of its neighbours.
  // Restore only live neighbours, never any other erased dot.
  for(const dot of removed){
    const reach=dot.r+DOT_SIZE.max/2+.15,cells=Math.ceil(reach/SPACING);
    for(let row=dot.row-cells;row<=dot.row+cells;row++)for(let column=dot.column-cells;column<=dot.column+cells;column++){
      const id=`${column}:${row}`,neighbour=byId.get(id);
      if(neighbour&&!erased.has(id)&&Math.hypot(neighbour.x-dot.x,neighbour.y-dot.y)<dot.r+neighbour.r+.15)restored.set(id,neighbour);
    }
  }
  return {removed,restored:[...restored.values()]};
}

export function transformedDotPoint(layer,point) {
  const scale=Math.max(.1,Math.min(3,finite(layer.scale,1))),angle=finite(layer.rotation,0)*Math.PI/180;
  const dx=((layer.dotFlipX===true?100-point.x:point.x)-50)*scale,dy=(point.y-75)*scale;
  return {x:finite(layer.x,50)+dx*Math.cos(angle)-dy*Math.sin(angle),y:finite(layer.y,75)+dx*Math.sin(angle)+dy*Math.cos(angle)};
}

// Same array return contract as eraseStrokeAt, for use with layers.flatMap().
// Removal is by dot identity, not by a paint-coloured patch, so the underlying
// material and any lower layers remain visible through the erased circle.
export function erasePatternDotsAt(layer,point,radius) {
  if(!layer||layer.kind!=='pattern'||layer.type!=='dots'||layer.visible===false||layer.opacity===0||!point||!Number.isFinite(point.x)||!Number.isFinite(point.y)||!Number.isFinite(radius)||radius<=0)return layer?[layer]:[];
  const scale=Math.max(.1,Math.min(3,finite(layer.scale,1)));
  const hits=patternDotPoints(layer).filter(dot=>{const p=transformedDotPoint(layer,dot);return Math.hypot(p.x-point.x,p.y-point.y)<=Math.min(radius,100)+dot.r*scale;});
  if(!hits.length)return [layer];
  const state=normalizeDotPattern(layer);
  return [{...layer,...state,erasedDots:[...new Set([...state.erasedDots,...hits.map(dot=>dot.id)])]}];
}
